// useGlobalFilter — URL-synced filter state for GlobalFilterBar.
//
// Reads initial state from `?` query params and writes updates back
// via history.replace so dashboards are shareable as URLs. Defaults
// for an unspecified param are inert (empty array / 30d).
//
// Why URL state and not Redux: the filter is page-local (different
// per analytics view). Persisting cross-page state would leak
// "watching Project A, last 7 days" into Project B and surprise
// users. URL state is naturally page-scoped.
import { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

const DEFAULT_TIME = "30d";

function parseList(searchParams, key) {
  const raw = searchParams.get(key);
  if (!raw) return [];
  return raw.split(",").filter(Boolean);
}

export function useGlobalFilter(initial = {}) {
  const history = useHistory();
  const location = useLocation();

  // Build initial state from the URL. ANY direct caller can pass
  // `initial` to seed defaults (e.g. preselecting today's user).
  const buildFromUrl = useCallback(() => {
    const sp = new URLSearchParams(location.search);
    return {
      timeRange:   sp.get("range") || initial.timeRange || DEFAULT_TIME,
      userIds:     parseList(sp, "users"),
      disciplines: parseList(sp, "disciplines"),
      milestones:  parseList(sp, "milestones"),
      revisionIds: parseList(sp, "revisions"),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const [filter, setFilter] = useState(buildFromUrl);

  // Whenever the URL changes externally (back/forward), pull state
  // from it. Without this, browser-back would visually update the
  // URL but leave the page rendering the old filter.
  useEffect(() => {
    setFilter(buildFromUrl());
  }, [buildFromUrl]);

  const writeToUrl = useCallback((next) => {
    const sp = new URLSearchParams(location.search);
    if (next.timeRange && next.timeRange !== DEFAULT_TIME) {
      sp.set("range", next.timeRange);
    } else {
      sp.delete("range");
    }
    for (const [paramKey, stateKey] of [
      ["users", "userIds"],
      ["disciplines", "disciplines"],
      ["milestones", "milestones"],
      ["revisions", "revisionIds"],
    ]) {
      const v = next[stateKey];
      if (Array.isArray(v) && v.length > 0) sp.set(paramKey, v.join(","));
      else sp.delete(paramKey);
    }
    const qs = sp.toString();
    history.replace(`${location.pathname}${qs ? `?${qs}` : ""}`);
  }, [history, location.pathname, location.search]);

  const onChange = useCallback((next) => {
    setFilter(next);
    writeToUrl(next);
  }, [writeToUrl]);

  const onReset = useCallback(() => {
    const cleared = {
      timeRange: DEFAULT_TIME,
      userIds: [],
      disciplines: [],
      milestones: [],
      revisionIds: [],
    };
    setFilter(cleared);
    writeToUrl(cleared);
  }, [writeToUrl]);

  return { filter, onChange, onReset };
}
